import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import Badge from 'components/Badge';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DeleteConfirmModal } from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import DeleteAnnualType from '../gql/DeleteAnnualType';
import { useGetAnnualCategories } from '../gql/UseGetAnnualCategories';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  CategoryName,
  BackButton,
} from './StyledComponents';

// Back arrow icon
const BackArrowIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

// Plus icon for create button
const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

// Tag icon for page header
const TagIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
    />
  </svg>
);

const CategoriesPageContent = (props) => {
  const { deleteAnnualType } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Get account_id from URL - this is the account we're viewing
  const { input } = useSearchInput();
  const accountId = input.account_id ? Number(input.account_id) : null;

  // Toaster state
  const [toaster, setToaster] = useState({ isOpen: false });

  // Show toaster from navigation state (e.g., after create/edit)
  useEffect(() => {
    if (location.state?.toaster) {
      setToaster({ isOpen: true, ...location.state.toaster });
      // Clear the state so it doesn't show again on refresh
      history.replace(location.pathname + location.search, {});
    }
  }, [location.state, history, location.pathname, location.search]);

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search and pagination
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Back navigation handler
  const handleBack = () => {
    if (accountId) {
      history.push(`${routePrefix}/accounts/${accountId}/annuals`);
    } else {
      history.goBack();
    }
  };

  // Fetch categories - use account_id from URL directly
  const { data, loading } = useGetAnnualCategories(accountId);

  const categories = data?.getAnnualTypesAdmin?.results ?? [];
  const totalCount = data?.getAnnualTypesAdmin?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    updateQuery({ queryString: searchTerm, page: 1 });
  };

  const handleEditCategory = (category) => {
    const editUrl = accountId
      ? `${routePrefix}/annualcategories/edit/${category.id}?account_id=${accountId}`
      : `${routePrefix}/annualcategories/edit/${category.id}`;
    history.push(editUrl);
  };

  const handleCreateCategory = () => {
    const addUrl = accountId
      ? `${routePrefix}/annualcategories/add?account_id=${accountId}`
      : `${routePrefix}/annualcategories/add`;
    history.push(addUrl);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteAnnualType(categoryToDelete.id, accountId);

      if (!response || response.errors) {
        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: 'Error deleting category.',
        });
      } else {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Success!',
          message: 'Category deleted successfully.',
        });
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Error deleting category.',
      });
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setCategoryToDelete(null);
  };

  // Table columns
  const columns = [
    {
      field: 'name',
      header: 'Category Name',
      render: (category) => <CategoryName>{category.name}</CategoryName>,
    },
    {
      field: 'account',
      header: 'Account',
      render: (category) => category.account?.business_dba || '—',
    },
    {
      field: 'type',
      header: 'Type',
      render: (category) =>
        category.type ? (
          <Badge variant="neutral" size="small">
            {category.type.charAt(0).toUpperCase() + category.type.slice(1)}
          </Badge>
        ) : (
          '—'
        ),
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditCategory,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  // Quick actions for sidebar
  const quickActions = [
    {
      label: 'Create Category',
      icon: <PlusIcon />,
      onClick: handleCreateCategory,
    },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <BackButton onClick={handleBack} title="Go back">
              <BackArrowIcon />
            </BackButton>
            <PageHeaderIcon>
              <TagIcon />
            </PageHeaderIcon>
            <HeaderTitle>Annual / Season Categories</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Data Table Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Search + Filters Row */}
                <PageHeader>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search categories..."
                  />
                </PageHeader>

                <DataTable
                  columns={columns}
                  data={categories}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No categories found"
                  emptySubtext="Try adjusting your filters or create a new category"
                  emptyIcon={
                    <svg
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  }
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            <QuickActions title="Quick Actions" actions={quickActions} />
          </Sidebar>
        </DashboardLayout>
      </Container>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalVisible}
        onClose={handleDeleteCancel}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryToDelete?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title || ''}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Wrap with theme provider
const CategoriesPage = (props) => (
  <EnhancedThemeProvider>
    <CategoriesPageContent {...props} />
  </EnhancedThemeProvider>
);

export default compose(DeleteAnnualType)(CategoriesPage);
